import { streamingRemoteFeedback, streamingResponseText, streamingFieldText, streamingFeedbackText } from "../i18n/streamingFeedbackPresentation";
import { useStandaloneLanguage, type StandaloneMessage, stLocal, stException, stFeedback, stMessage } from "../i18n/streamingPagesPresentation";
/**
 * useUserDevices -- React hook for fetching WSD-tracked device records for a user.
 *
 * Endpoint:
 * - GET /api/streaming/users/:serverType/:userId/devices
 *
 * Returns device records tracked by WSD for the given upstream user.
 */
import { useCallback, useEffect, useRef, useState } from "react";

// ============================================================================
// Types (mirrored from backend WsdDeviceRecord)
// ============================================================================

export interface WsdDevice {
  id: number;
  server_type: string;
  username: string;
  device_id: string;
  device_name: string;
  custom_name: string | null;
  ip_address: string | null;
  first_seen: number;
  last_seen: number;
  connection_count: number;
  user_agent: string | null;
  client_version: string | null;
  platform: string | null;
  is_active: number;
  metadata: string | null;
  isLocked?: boolean;
}

// ============================================================================
// Cache
// ============================================================================

interface CachedDevices {
  devices: WsdDevice[];
  fetchedAt: number;
}

const devicesCache = new Map<string, CachedDevices>();
const CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes

function buildDevicesCacheKey(serverType: string, userId: string): string {
  return `devices:${serverType}:${userId}`;
}

// ============================================================================
// useUserDevices
// ============================================================================

export function useUserDevices(
  serverType: string,
  userId: string,
  enabled: boolean = false
): {
  devices: WsdDevice[] | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const [devices, setDevices] = useState<WsdDevice[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorFeedback, setError] = useState<string | StandaloneMessage | null>(null);
  useStandaloneLanguage();
  const error = stMessage(errorFeedback);
  const abortRef = useRef<AbortController | null>(null);
  const fetchedKeyRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);

  const fetchDevices = useCallback(
    async (force?: boolean) => {
      if (!serverType || !userId || !enabled) return;

      const key = buildDevicesCacheKey(serverType, userId);

      // Check cache (unless forced)
      if (!force) {
        const cached = devicesCache.get(key);
        if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
          setDevices(cached.devices);
          setError(null);
          fetchedKeyRef.current = key;
          return;
        }
      }

      // Skip if already fetching this key
      if (isFetchingRef.current && fetchedKeyRef.current === key) return;

      // Abort any in-flight request
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      isFetchingRef.current = true;
      setIsLoading(true);
      setError(null);

      try {
        const url = `/api/streaming/users/${encodeURIComponent(serverType)}/${encodeURIComponent(userId)}/devices`;
        const res = await fetch(url, { signal: controller.signal });
        const data = await res.json();

        if (!res.ok || !data.ok || !data.data?.devices) {
          throw stException((streamingRemoteFeedback(data) ?? data.message ?? stLocal("failedToLoadUserDevices")));
        }

        const fetchedDevices = data.data.devices as WsdDevice[];
        setDevices(fetchedDevices);
        setError(null);

        // Cache
        devicesCache.set(key, {
          devices: fetchedDevices,
          fetchedAt: Date.now(),
        });
        fetchedKeyRef.current = key;
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        const message = stFeedback(err, "failedToLoadUserDevices");
        setError(message);
      } finally {
        isFetchingRef.current = false;
        setIsLoading(false);
      }
    },
    [serverType, userId, enabled]
  );

  // Fetch on mount or when key changes
  useEffect(() => {
    if (!enabled) {
      setDevices(null);
      setError(null);
      fetchedKeyRef.current = null;
      return;
    }

    const key = buildDevicesCacheKey(serverType, userId);
    if (fetchedKeyRef.current !== key) {
      void fetchDevices();
    }

    return () => {
      abortRef.current?.abort();
    };
  }, [serverType, userId, enabled, fetchDevices]);

  const refetch = useCallback(() => {
    void fetchDevices(true);
  }, [fetchDevices]);

  return { devices, isLoading, error, refetch };
}
